import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Typography,
} from '@mui/material';
import { Create, Gesture } from '@mui/icons-material';
import SignaturePad from './SignaturePad';
import { renderCursiveSignatureDataUrl } from '../utils/trainingSignatureUtils';

const TrainingFieldSignDialog = ({
  open,
  onClose,
  field,
  defaultName,
  onSave,
}) => {
  const [mode, setMode] = useState('draw');
  const [typedName, setTypedName] = useState('');
  const [signatureImage, setSignatureImage] = useState(null);
  const [loading, setLoading] = useState(false);

  const isInitial = field?.field_type === 'initial';
  const title = isInitial ? 'Add initial' : 'Add signature';

  useEffect(() => {
    if (!open) return;
    setMode('draw');
    setTypedName(defaultName || '');
    setSignatureImage(null);
  }, [open, defaultName, field?.id]);

  useEffect(() => {
    if (mode !== 'cursive' || !open) return undefined;
    const text = typedName.trim();
    if (!text) {
      setSignatureImage(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    renderCursiveSignatureDataUrl(text)
      .then((dataUrl) => {
        if (!cancelled) setSignatureImage(dataUrl);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [mode, typedName, open]);

  const handleSave = () => {
    if (!signatureImage) return;
    onSave?.(signatureImage);
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}{field?.label ? `: ${field.label}` : ''}</DialogTitle>
      <DialogContent>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={(_, next) => {
            if (!next) return;
            setMode(next);
            setSignatureImage(null);
          }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="draw">
            <Gesture sx={{ fontSize: 18, mr: 0.5 }} />
            Draw
          </ToggleButton>
          <ToggleButton value="cursive">
            <Create sx={{ fontSize: 18, mr: 0.5 }} />
            Typed
          </ToggleButton>
        </ToggleButtonGroup>

        {mode === 'cursive' && (
          <TextField
            fullWidth
            size="small"
            label={isInitial ? 'Initials' : 'Signature text'}
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            sx={{ mb: 2 }}
          />
        )}

        {mode === 'draw' ? (
          <SignaturePad onChange={setSignatureImage} height={140} />
        ) : (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              minHeight: 140,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            {loading ? (
              <Typography variant="body2" color="text.secondary">Preparing…</Typography>
            ) : signatureImage ? (
              <Box
                component="img"
                src={signatureImage}
                alt="Signature preview"
                sx={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain' }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">Enter text above</Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!signatureImage} onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrainingFieldSignDialog;
